/**
 * Panel de salud del sync.
 *
 * La pregunta que tiene que contestar en dos segundos: **¿está corriendo?**
 * Todo lo demás es secundario.
 */

import { useState, useEffect } from 'react'
import type { SaludSync, Corrida } from './tipos.ts'
import { pedirConSesion } from './Entrar.tsx'
import { fmt, hora, fechaCorta } from './formato.ts'

/** Con el sync cada 10 min, pasar de 25 significa que el worker está caído. */
const MINUTOS_PARA_ALARMA = 25

type SaludRespaldo = {
  hay: boolean
  cuantos: number
  ultimo?: { nombre: string; bytes: number; cuando: string }
  horas_desde_el_ultimo?: number | null
  al_dia?: boolean
  /** La última vez que se comprobó que el respaldo VUELVE, y cuánto tardó. */
  restauracion?: {
    cuando: string
    segundos: number
    respaldo: string
    remesas: number
    entregas: number
    evidencias: number
  } | null
}

/**
 * ¿Hay respaldo, y de cuándo?
 *
 * 🚨 Se puso porque NO HABÍA NINGUNO y nadie se había dado cuenta: ni carpeta de
 * respaldos ni tarea programada, con 5.058 remesas y las entregas con su foto y
 * su firma dentro. Un respaldo que nadie mira es un respaldo que el día que
 * falla no avisa.
 */
function Respaldo() {
  const [r, setR] = useState<SaludRespaldo | null>(null)

  useEffect(() => {
    void pedirConSesion<SaludRespaldo>('/api/salud-respaldo')
      .then(setR)
      .catch(() => setR({ hay: false, cuantos: 0 }))
  }, [])

  if (!r) return null

  const bien = r.hay && r.al_dia === true
  return (
    <section className="tarjeta p-5">
      <div className="flex items-start gap-3">
        <span
          className="mt-1.5 inline-block h-3 w-3 shrink-0 rounded-full"
          style={{ background: bien ? '#3BBA74' : '#CA2B2B' }}
        />
        <div>
          <h3 className="text-[17px] font-semibold">
            {!r.hay
              ? 'NO hay respaldo de la base'
              : bien
                ? 'La base y las fotos están respaldadas'
                : 'El respaldo está atrasado'}
          </h3>
          <p className="mt-1 text-[15px] text-tinta-suave">
            {!r.hay ? (
              'Si el disco falla o alguien borra algo, no hay de dónde recuperar las remesas ni las entregas. Avísale a soporte.'
            ) : (
              <>
                Último:{' '}
                <strong className="text-tinta">
                  {r.horas_desde_el_ultimo === 0
                    ? 'hace menos de una hora'
                    : `hace ${r.horas_desde_el_ultimo} h`}
                </strong>{' '}
                · {Math.round((r.ultimo?.bytes ?? 0) / 1024 / 1024)} MB · se guardan {r.cuantos}{' '}
                {r.cuantos === 1 ? 'copia' : 'copias'}. Corre solo{' '}
                <strong className="text-tinta">cada 3 horas</strong>, con la base y las fotos.
              </>
            )}
          </p>

          {/*
            🚨 Tener copias no es tenerlas PROBADAS.
            Un respaldo que nunca se restauró no es un respaldo: el día que se
            cae el servidor no es momento de averiguar si vuelve. Aqui se dice
            cuándo se comprobó y CUÁNTO TARDÓ, que es lo que se pregunta.
          */}
          {r.hay && (
            <p className="mt-2 text-[15px] text-tinta-suave">
              {r.restauracion ? (
                <>
                  <strong className="text-tinta">Probado: vuelve.</strong> El{' '}
                  {new Date(r.restauracion.cuando).toLocaleDateString('es-CO', {
                    day: 'numeric',
                    month: 'long',
                  })}{' '}
                  se restauró en una base limpia en{' '}
                  <strong className="text-tinta">
                    {r.restauracion.segundos <= 1
                      ? 'menos de un segundo'
                      : `${r.restauracion.segundos} segundos`}
                  </strong>
                  , con {r.restauracion.remesas.toLocaleString('es-CO')} remesas y{' '}
                  {r.restauracion.evidencias} evidencias adentro.
                </>
              ) : (
                <span style={{ color: 'var(--color-ambar-texto)' }}>
                  Nunca se ha probado restaurarlo. Hay copias, pero nadie ha comprobado que
                  vuelvan.
                </span>
              )}
            </p>
          )}
        </div>
      </div>
    </section>
  )
}

type ReglaDura = { regla: string; cumple: boolean; cuantos_mal: number; detalle: string }

/**
 * Las reglas duras, comprobadas contra los datos de verdad.
 *
 * 🚨 Nace de dos hallazgos seguidos: el respaldo estaba marcado como funcionando
 * y **no corría**, y la evidencia estaba escrita como inmutable y **se dejaba
 * modificar**. Las dos veces el documento decía una cosa y la base otra.
 *
 * Esto lo comprueba cada vez que alguien abre esta pantalla, con los datos que
 * hay — no con los que debería haber.
 */
function Reglas() {
  const [r, setR] = useState<{ reglas: ReglaDura[]; todas_bien: boolean } | null>(null)

  useEffect(() => {
    void pedirConSesion<{ reglas: ReglaDura[]; todas_bien: boolean }>('/api/salud-reglas')
      .then(setR)
      .catch(() => setR(null))
  }, [])

  if (!r) return null

  const rotas = r.reglas.filter((x) => !x.cumple).length

  return (
    <section className="tarjeta p-5">
      <div className="flex items-start gap-3">
        <span
          className="mt-1.5 inline-block h-3 w-3 shrink-0 rounded-full"
          style={{ background: r.todas_bien ? '#3BBA74' : '#CA2B2B' }}
        />
        <div className="min-w-0">
          <h3 className="text-[17px] font-semibold">
            {/*
              ⛔ El titular DICE CUÁNTAS. Decía «Hay una regla rota» siempre,
              con una o con cinco: quien lo lee de lejos entiende que es una
              sola y no baja a contarlas.
            */}
            {r.todas_bien
              ? 'Las reglas del proyecto se están cumpliendo'
              : rotas === 1
                ? 'Hay 1 regla rota'
                : `Hay ${rotas} reglas rotas`}
          </h3>
          <ul className="mt-2 flex flex-col gap-1.5">
            {r.reglas.map((x) => (
              <li key={x.regla} className="flex items-baseline gap-2 text-[15px]">
                <span
                  className="shrink-0 font-semibold"
                  style={{ color: x.cumple ? '#1F6E45' : '#CA2B2B' }}
                >
                  {x.cumple ? '✓' : '✗'}
                </span>
                <span>
                  {x.regla}
                  {!x.cumple && <strong className="text-[#CA2B2B]"> · {x.cuantos_mal} mal</strong>}
                  <span className="block text-[13px] text-tinta-suave">{x.detalle}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  )
}

/**
 * 📍 CUÁNTA EVIDENCIA TRAE SU SITIO.
 *
 * 🚨 Luis, 24-ago-2026: «el sistema **siempre** debe guardar la hora, el sitio
 * y la GPS».
 *
 * Esto existe para que ese «siempre» **se pueda mirar**. Antes de esta pantalla
 * el dato estaba en la base y nadie lo veía: medido el 24-ago, los cumplidos
 * subidos después iban por **0 de 25** con punto —ese camino ni siquiera pedía
 * la ubicación— y nadie se había dado cuenta en cinco meses.
 *
 * ⛔ Y ojo con leerlo mal: un porcentaje bajo NO siempre es una falla del
 * sistema. Una bodega techada, un sótano o un celular con el permiso apagado
 * dan el mismo cero. Por eso al lado va la precisión media: si los puntos que
 * sí llegan son buenos, el problema es de cobertura, no de la app.
 */
function Ubicaciones() {
  const [r, setR] = useState<{
    evidencia: {
      que: string
      total: number
      con_punto: number
      porcentaje: number
      precision_media_m: number | null
      con_hora_del_celular: number
      con_desfase: number
      ultimo_incompleto: string | null
    }[]
    perdidos: { cuantos: number; revisados: number; ejemplo: string | null } | null
    huellas: {
      revisadas: number
      correctas: number
      alteradas: number
      ejemplo: string | null
    } | null
  } | null>(null)

  useEffect(() => {
    void pedirConSesion<{
      evidencia: {
        que: string
        total: number
        con_punto: number
        porcentaje: number
        precision_media_m: number | null
        con_hora_del_celular: number
        con_desfase: number
        ultimo_incompleto: string | null
      }[]
      perdidos: { cuantos: number; revisados: number; ejemplo: string | null } | null
      huellas: {
        revisadas: number
        correctas: number
        alteradas: number
        ejemplo: string | null
      } | null
    }>('/api/salud-evidencia')
      .then(setR)
      .catch(() => setR(null))
  }, [])

  if (!r || r.evidencia.length === 0) return null

  return (
    <section className="tarjeta p-5">
      <h3 className="text-[17px] font-semibold">Cuánta evidencia trae su sitio</h3>
      <p className="mt-1 text-[13px] text-tinta-suave">
        La regla es que <b>siempre</b> se guarde la hora, el sitio y el punto del GPS. Aquí se
        ve si se está cumpliendo, con los datos que hay.
      </p>
      {/*
        🚨🚨 LO MÁS GRAVE DE ESTA PANTALLA VA PRIMERO.
        Una foto que la base promete y el disco no tiene es una entrega que no
        se puede defender si el cliente reclama — y el sistema decía que sí la
        tenía. Se descubrió el 24-ago-2026 cotejándolo a mano; ahora se ve.
      */}
      {r.perdidos && r.perdidos.cuantos > 0 && (
        <div className="mt-3 rounded-lg p-3" style={{ background: '#FDECEC' }}>
          <p className="text-[15px] font-semibold" style={{ color: '#CA2B2B' }}>
            {r.perdidos.cuantos === 1
              ? '1 foto está registrada pero su archivo no está en el servidor'
              : `${r.perdidos.cuantos} fotos están registradas pero sus archivos no están en el servidor`}
          </p>
          <p className="mt-1 text-[13px] text-tinta-suave">
            Revisadas las {r.perdidos.revisados} más recientes. Esas entregas no se pueden
            defender si el cliente reclama: hay que pedirle otra foto al conductor.
            {r.perdidos.ejemplo ? ` Por ejemplo: ${r.perdidos.ejemplo}` : ''}
          </p>
        </div>
      )}
      {r.perdidos && r.perdidos.cuantos === 0 && (
        <p className="mt-3 text-[14px]" style={{ color: '#1F6E45' }}>
          ✓ Las {r.perdidos.revisados} fotos más recientes están todas en el servidor.
        </p>
      )}

      {/*
        🔐 Y que las que están sean LAS MISMAS que se guardaron.
        De cada foto se guarda su huella. Si alguien cambiara el archivo en el
        disco, la huella dejaría de cuadrar y la prueba no valdría. Nunca se
        había comparado: se comprobó a mano el 24-ago-2026 —151 de 151
        correctas— y desde entonces se comprueba solo.
      */}
      {r.huellas && r.huellas.alteradas > 0 && (
        <div className="mt-2 rounded-lg p-3" style={{ background: '#FDECEC' }}>
          <p className="text-[15px] font-semibold" style={{ color: '#CA2B2B' }}>
            {r.huellas.alteradas === 1
              ? '1 foto NO es la misma que se guardó'
              : `${r.huellas.alteradas} fotos NO son las mismas que se guardaron`}
          </p>
          <p className="mt-1 text-[13px] text-tinta-suave">
            El archivo del disco no cuadra con la huella que quedó registrada al subirlo. Esa
            prueba no se puede sostener.
            {r.huellas.ejemplo ? ` Por ejemplo: ${r.huellas.ejemplo}` : ''}
          </p>
        </div>
      )}
      {r.huellas && r.huellas.alteradas === 0 && r.huellas.revisadas > 0 && (
        <p className="mt-1 text-[14px]" style={{ color: '#1F6E45' }}>
          ✓ Las {r.huellas.revisadas} más recientes son idénticas a como se guardaron: ninguna
          se cambió.
        </p>
      )}

      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-[15px]" style={{ borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', padding: '0 12px 6px 0' }}>QUÉ</th>
              <th style={{ textAlign: 'right', padding: '0 12px 6px 0' }}>TOTAL</th>
              <th style={{ textAlign: 'right', padding: '0 12px 6px 0' }}>CON PUNTO</th>
              <th style={{ textAlign: 'right', padding: '0 12px 6px 0' }}>PRECISIÓN</th>
              <th style={{ textAlign: 'right', padding: '0 12px 6px 0' }}>HORA DEL CELULAR</th>
              <th style={{ textAlign: 'right', padding: '0 0 6px 0' }}>DESFASE DEL RELOJ</th>
            </tr>
          </thead>
          <tbody>
            {r.evidencia.map((f) => (
              <tr key={f.que}>
                <td style={{ padding: '7px 12px 7px 0' }}>{f.que}</td>
                <td className="num" style={{ textAlign: 'right', padding: '7px 12px 7px 0' }}>
                  {f.total}
                </td>
                <td
                  className="num"
                  style={{
                    textAlign: 'right',
                    padding: '7px 12px 7px 0',
                    /* ⛔ Rojo SOLO para problemas: por debajo de la mitad. */
                    color: f.porcentaje < 50 ? '#CA2B2B' : '#1F6E45',
                    fontWeight: 600,
                  }}
                >
                  {f.con_punto} · {f.porcentaje} %
                </td>
                <td
                  className="num"
                  style={{ textAlign: 'right', padding: '7px 12px 7px 0', color: '#5A6A83' }}
                >
                  {f.precision_media_m === null ? '—' : `${f.precision_media_m} m`}
                </td>
                {/*
                  🚨 «73» SOLO NO DICE NADA: hay que saber de cuántos.
                  Estas dos columnas no miden cobertura de GPS —que depende de
                  una bodega techada o un permiso apagado— sino si el sistema
                  GUARDA lo que tiene que guardar. Aquí no hay excusa de
                  cobertura: si no está completo, es un defecto del código.
                */}
                <td
                  className="num"
                  style={{
                    textAlign: 'right',
                    padding: '7px 12px 7px 0',
                    color: f.con_hora_del_celular < f.total ? '#CA2B2B' : '#1F6E45',
                    fontWeight: f.con_hora_del_celular < f.total ? 600 : 400,
                  }}
                >
                  {f.con_hora_del_celular} de {f.total}
                </td>
                <td
                  className="num"
                  style={{
                    textAlign: 'right',
                    padding: '7px 0',
                    color: f.con_desfase < f.total ? '#CA2B2B' : '#1F6E45',
                    fontWeight: f.con_desfase < f.total ? 600 : 400,
                  }}
                >
                  {f.con_desfase} de {f.total}
                  {f.con_desfase < f.total && f.ultimo_incompleto ? (
                    <span className="block text-[12px] font-normal text-[#5A6A83]">
                      el último, {fechaCorta(f.ultimo_incompleto)}
                    </span>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

export function PanelSalud({ datos }: { datos: SaludSync }) {
  const minutos = Math.floor(datos.segundosDesdeUltima / 60)
  const vivo = datos.segundosDesdeUltima >= 0 && minutos < MINUTOS_PARA_ALARMA
  const ultima = datos.corridas[0]
  /*
   * 🚨 Si la paginación de Vulcano cuadró o no, SOLO lo sabe un barrido
   * completo. El corto de cada 10 minutos nunca cuenta como completo —no va
   * por todo, a propósito— así que juzgarlo con él dejaba esa advertencia
   * encendida para siempre, dijera lo que dijera Vulcano.
   */
  const ultimoCompleto = datos.corridas.find((c) => !esBarridoCorto(c))

  return (
    <section>
      <div className="tit-fila">
        <h1>Salud del sync</h1>
        <span>
          El barrido que trae los viajes de Vulcano. Si esto se cae, la consola muestra datos
          viejos sin avisar.
        </span>
      </div>

      <Respaldo />
      <Reglas />
      <Ubicaciones />

      {/* --- Lo primero que se ve: ¿está vivo? --- */}
      <section className="tarjeta p-5">
        <div className="flex flex-wrap items-center gap-x-8 gap-y-4">
          <div className="flex items-center gap-3">
            <span
              className="punto"
              style={{ background: vivo ? '#319B61' : '#CA2B2B', width: 10, height: 10 }}
            />
            <div>
              <div className="text-[20px] font-semibold leading-tight">
                {vivo ? 'El sync está corriendo' : 'El sync no está corriendo'}
              </div>
              <div className="mt-0.5 text-[14px] text-[#5A6A83]">
                {datos.segundosDesdeUltima < 0
                  ? 'Todavía no ha corrido ninguna vez'
                  : `Último barrido hace ${minutos < 1 ? 'menos de un minuto' : `${minutos} min`}`}
                {/* La cadencia que pidió Luis, dicha entera: si solo se dice
                    «cada 10 minutos», los números del barrido corto no cuadran
                    con nada. */}
                {' · corto cada 10 min (últimos 2 meses) · completo cada hora'}
              </div>
            </div>
          </div>

          {ultima && (
            <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
              <Dato
                etiqueta="Barrido"
                valor={esBarridoCorto(ultima) ? 'Corto' : 'Completo'}
              />
              <Dato etiqueta="Duración" valor={`${ultima.segundos ?? '—'} s`} />
              {/* 🚨 Al corto NO se le pone denominador: leyó las que tenía que
                  leer. Decir «18/52» lo hace ver como uno que falló. */}
              <Dato
                etiqueta="Páginas"
                valor={
                  esBarridoCorto(ultima)
                    ? String(ultima.paginas_leidas)
                    : `${ultima.paginas_leidas}/${ultima.paginas_esperadas}`
                }
              />
              <Dato etiqueta="Pedidos" valor={fmt(ultima.pedidos_vistos)} />
              <Dato etiqueta="Remesas" valor={fmt(ultima.remesas_vistas)} />
            </div>
          )}
        </div>

        {/* La advertencia de paginación, explicada en español y sin gritar. */}
        {ultimoCompleto && !ultimoCompleto.completo && (
          <div className="mt-4 rounded-[4px] bg-[#FDF4E3] p-3">
            <div className="flex items-center gap-2">
              <span className="punto" style={{ background: '#A4600D' }} />
              <span className="text-[14px] font-medium text-[#A4600D]">
                Vulcano no entrega todos los pedidos
              </span>
            </div>
            <p className="mt-1 text-[14px] leading-relaxed text-[#5A6A83]">
              Su paginación repite unos registros y se salta otros, siempre los mismos. Por eso{' '}
              <strong className="font-medium text-[#121B2B]">
                no se marca ninguna remesa como anulada
              </strong>
              : marcar como anulada una remesa que existe sería peor que no detectar las que sí se
              anularon. Se resuelve cuando Vulcano ordene sus resultados con un desempate.
            </p>
          </div>
        )}
      </section>

      {/*
        --- 🚨 Lo que llegó y no entendemos ---

        Es lo primero de la pantalla cuando suena, y por buena razón: el mismo
        problema costó tres veces en una semana. El sistema guarda en nulo todo
        valor que no conoce, y **un nulo silencioso no se nota hasta que algo
        desaparece de las pantallas** — 207 remesas se volvieron invisibles así,
        y un conductor pasó semanas viendo 6 viajes cuando tenía 13.

        ⛔ Va en rojo y arriba, no como una nota al pie: cuando esto aparece,
        hay carga que ya no se está viendo en alguna parte.
      */}
      {datos.sinEntender.length > 0 && (
        <section className="caja" style={{ borderColor: '#E9B8B8' }}>
          <div className="caja-tit">
            <h2 style={{ color: 'var(--color-rojo)' }}>
              Llegó algo que el sistema no conoce
            </h2>
            <small>últimos 30 días</small>
          </div>
          <div style={{ padding: '12px 16px 16px' }}>
            <p style={{ fontSize: 13.5, color: '#46536b', lineHeight: 1.45, marginBottom: 12 }}>
              El sistema de la empresa está mandando valores nuevos. Mientras no se agreguen,
              <b> esos despachos se guardan sin ese dato y pueden no aparecer en las pantallas</b>.
            </p>
            <div className="tabla-caja" style={{ border: 'none' }}>
              <table>
                <thead>
                  <tr>
                    <th>Qué campo</th>
                    <th>Qué mandaron</th>
                    <th className="text-right">Cuántos</th>
                    <th>Última vez</th>
                  </tr>
                </thead>
                <tbody>
                  {datos.sinEntender.map((v) => (
                    <tr key={`${v.que}-${v.valor}`}>
                      <td>{v.que}</td>
                      <td>
                        <span className="et et-mal">{v.valor}</span>
                      </td>
                      <td className="num text-right">{Number(v.cuantas)}</td>
                      <td className="num">{v.visto_por_ultima_vez?.slice(0, 16)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      {/* --- Qué hay espejado --- */}
      <section className="grid grid-cols-2 gap-4 md:grid-cols-4 xl:grid-cols-7">
        <Tarjeta etiqueta="Pedidos" valor={datos.totales.pedidos} />
        <Tarjeta etiqueta="Remesas" valor={datos.totales.remesas} />
        <Tarjeta etiqueta="Clientes" valor={datos.totales.clientes} />
        <Tarjeta etiqueta="Vehículos" valor={datos.totales.vehiculos} />
        <Tarjeta etiqueta="Conductores" valor={datos.totales.conductores} />
        <Tarjeta etiqueta="Vínculos activos" valor={datos.totales.vinculos_vigentes} />
        <Tarjeta
          etiqueta="Anuladas"
          valor={datos.totales.remesas_anuladas}
          alerta={Number(datos.totales.remesas_anuladas) > 0}
        />
        {/* Que se pueda ver que el chequeo está corriendo, no solo cuando falla. */}
        <Tarjeta
          etiqueta="Datos sin entender"
          valor={datos.sinEntender.length === 0 ? 'ninguno' : String(datos.sinEntender.length)}
          alerta={datos.sinEntender.length > 0}
        />
      </section>

      <div className="grid gap-5 xl:grid-cols-[2fr_1fr]">
        {/* --- Historial de corridas --- */}
        <section className="caja">
          <div className="caja-tit">
            <h2>Últimos barridos</h2>
            <small>uno cada 10 minutos</small>
          </div>
          <div className="tabla-caja tabla-scroll" style={{ border: 'none', borderRadius: 0 }}>
            <table>
              <thead>
                <tr>
                  <th>Hora</th>
                  <th>Estado</th>
                  <th className="text-right">Páginas</th>
                  <th className="text-right">Pedidos</th>
                  <th className="text-right">Nuevos</th>
                  <th className="text-right">Cambiados</th>
                  <th className="text-right">Sin cambio</th>
                  <th className="text-right">Duración</th>
                </tr>
              </thead>
              <tbody>
                {datos.corridas.map((c) => (
                  <Fila key={c.id} c={c} />
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* --- Detección pasiva de contenedor (tarea 1.15) --- */}
        <section className="caja">
          <div className="caja-tit">
            <h2>Tipos de carga</h2>
            <small>llegan como códigos, sin nombre</small>
          </div>
          {/* El contenedor con scroll propio es obligatorio: sin él, esta tabla
              empujaba la PÁGINA a scroll horizontal en 768 y 1024 px. */}
          <div className="tabla-caja tabla-scroll" style={{ border: 'none', borderRadius: 0 }}>
            <table>
              <thead>
                <tr>
                  <th>Código</th>
                  <th className="text-right">Pedidos</th>
                  <th className="text-right">Últimos 30 días</th>
                </tr>
              </thead>
              <tbody>
                {datos.tiposDeCarga.map((t) => (
                  <tr key={t.codigo}>
                    <td className="num font-medium">{t.codigo}</td>
                    <td className="num text-right">{fmt(t.total)}</td>
                    <td className="num text-right">{fmt(t.ultimos_30d)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </section>
  )
}

/**
 * ¿Es el barrido corto —el de cada 10 minutos— o el completo?
 *
 * 🚨 Los dos leen la misma API pero no buscan lo mismo: el corto trae solo los
 * últimos 2 meses (18 páginas hoy) y el completo trae todo (52). Medirlos con
 * la misma vara hace ver al corto como un completo que falló.
 */
function esBarridoCorto(c: { tipo: string }): boolean {
  return c.tipo === 'SUPERFICIAL'
}

function Fila({ c }: { c: Corrida }) {
  const corto = esBarridoCorto(c)
  /* Un barrido CORTO nunca es «parcial»: no le faltó nada, no iba por todo. */
  const parcial = !corto && c.paginas_leidas < c.paginas_esperadas
  return (
    <tr>
      <td className="num whitespace-nowrap">{hora(c.iniciado_at)}</td>
      <td>
        {c.errores > 0 ? (
          <span className="estado estado-mal">Con errores</span>
        ) : corto ? (
          <span className="estado estado-ok">Corto</span>
        ) : c.completo ? (
          <span className="estado estado-ok">Completo</span>
        ) : parcial ? (
          <span className="estado estado-neutro">Parcial</span>
        ) : (
          <span className="estado estado-aviso">Faltaron pedidos</span>
        )}
      </td>
      <td className="num text-right">
        {corto ? c.paginas_leidas : `${c.paginas_leidas}/${c.paginas_esperadas}`}
      </td>
      <td className="num text-right">{fmt(c.pedidos_vistos)}</td>
      <td className="num text-right">{c.nuevos > 0 ? fmt(c.nuevos) : '—'}</td>
      <td className="num text-right">{c.cambiados > 0 ? fmt(c.cambiados) : '—'}</td>
      <td className="num text-right text-[#5A6A83]">{fmt(c.sin_cambio)}</td>
      <td className="num text-right">{c.segundos ?? '—'} s</td>
    </tr>
  )
}

function Tarjeta({
  etiqueta,
  valor,
  alerta = false,
}: {
  etiqueta: string
  valor: string
  alerta?: boolean
}) {
  return (
    <div className="tarjeta p-4">
      <div className="etiqueta">{etiqueta}</div>
      <div
        className="num mt-1 text-[20px] font-semibold leading-none"
        style={alerta ? { color: '#CA2B2B' } : undefined}
      >
        {fmt(valor)}
      </div>
    </div>
  )
}

function Dato({ etiqueta, valor }: { etiqueta: string; valor: string }) {
  return (
    <div>
      <div className="etiqueta">{etiqueta}</div>
      <div className="num mt-0.5 text-[16px] font-semibold">{valor}</div>
    </div>
  )
}
